import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { CircleX, type LucideIcon } from 'lucide-react';

import { AJUDA_DA_OPCAO_UNICA, obrigatoriaNaMesa, opcaoUnica } from '@/lib/escolha-unica';
import type { AlvoDoAdesivo } from '@/lib/leitura-do-adesivo';

import { ListaDeEscolha } from './ListaDeEscolha';
import { OlhoDaSenha } from './OlhoDaSenha';

/**
 * Campo de formulario e de filtro (v2.1, p.22).
 *
 * TRES REGRAS QUE NAO SAO ESTILO
 *
 * 1. Valida AO SAIR DO CAMPO, nunca a cada tecla. Quem digita "48" a caminho
 *    de "4832" nao errou nada; o sistema que grita no segundo caractere ensina
 *    a pessoa a ignorar mensagem de erro — e ai ela ignora a que importa.
 *
 * 2. Erro NO LUGAR DA AJUDA, abaixo do campo. Num cadastro de quinze campos,
 *    alerta no topo obriga a pessoa a descobrir sozinha qual deles a mensagem
 *    esta descrevendo. O topo do formulario e so para a recusa do servidor
 *    (p.22, p.31).
 *
 * 3. Marca o que e OPCIONAL, nunca o que e obrigatorio. Quase tudo neste
 *    sistema e obrigatorio; tela cheia de asterisco deixa de comunicar
 *    qualquer coisa, e a excecao e que precisa ficar visivel.
 *
 * 4. Marca o que veio PRE-PREENCHIDO pelo contexto. Um campo ja respondido
 *    economiza digitacao, mas so enquanto a pessoa souber que aquilo foi o
 *    sistema que escreveu — campo preenchido sem marca ninguem confere, e
 *    ninguem confere justamente o que o sistema errou sozinho. A marca some
 *    assim que a pessoa mexe no campo: dali em diante a resposta e dela.
 *
 * O rotulo fica ACIMA, em 12/500 no texto de lista e sem caixa alta — caixa
 * alta e so do sobretitulo (p.59). Opcional leva "· opcional" ao lado; o
 * obrigatorio nao leva asterisco. O erro e o vermelho de retorno (`--erro`,
 * p.12), com o icone de erro do vocabulario (p.19).
 */
export function Campo({
  id,
  rotulo,
  opcional = false,
  preenchido = false,
  ajuda,
  erro,
  etiqueta,
  children,
}: {
  readonly id: string;
  readonly rotulo: string;
  readonly opcional?: boolean;
  /** `true` enquanto o valor for o que o sistema escreveu, nao o que a pessoa digitou. */
  readonly preenchido?: boolean;
  /** Frase completa, com ponto final. Some quando ha erro. */
  readonly ajuda?: string;
  /** Diz O QUE FAZER, nao so o que deu errado. */
  readonly erro?: string | null;
  /**
   * O que fica AO LADO do rotulo, na mesma linha: a ultima leitura da maquina
   * no campo do medidor (decisao 54). Quebra para baixo do rotulo quando nao
   * cabe, e nunca entra no `<label>` — o nome do campo continua so o rotulo.
   */
  readonly etiqueta?: ReactNode;
  readonly children: ReactNode;
}) {
  const rotuloDoCampo = (
    <label htmlFor={id} className="flex flex-wrap items-baseline gap-x-1">
      <span className="txt-rotulo-campo">{rotulo}</span>
      {opcional ? <span className="txt-ajuda">· opcional</span> : null}
      {preenchido ? <span className="txt-ajuda">· pré-preenchido</span> : null}
    </label>
  );
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {/* "· opcional" e "· pré-preenchido" ficam mais leves que o rotulo pelo
          PESO (400 contra 500) e pela cor secundaria, que passa 4,5:1 no
          formulario montado sobre o fundo da pagina. */}
      {etiqueta ? (
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          {rotuloDoCampo}
          {etiqueta}
        </div>
      ) : (
        rotuloDoCampo
      )}

      {children}

      {erro ? (
        <span id={`${id}-erro`} role="alert" className="txt-ajuda flex items-start gap-1 text-erro">
          <CircleX aria-hidden="true" className="mt-px size-[var(--icone-controle)] shrink-0 text-erro" />
          <span>{erro}</span>
        </span>
      ) : ajuda ? (
        <span className="txt-ajuda">{ajuda}</span>
      ) : null}
    </div>
  );
}

/**
 * O que o campo guarda. Nao e estilo: e o que decide o teclado do aparelho, o
 * que o navegador aceita guardar e se o valor aparece na tela enquanto se
 * digita. `senha` existe desde que o sistema ganhou entrada de verdade — um
 * `<input type="text">` para senha a deixa legivel por cima do ombro e impede
 * o gerenciador de senhas de reconhecer o campo.
 */
export type TipoDeCampo = 'texto' | 'email' | 'senha';

const TIPO_HTML: Record<TipoDeCampo, string> = {
  texto: 'text',
  email: 'email',
  senha: 'password',
};

export function CampoTexto({
  rotulo,
  valor,
  aoMudar,
  placeholder,
  opcional = false,
  ajuda,
  validar,
  atalho,
  aplicado = false,
  tipo = 'texto',
  autoComplete,
  nome,
  idFixo,
  maxLength,
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly placeholder?: string;
  readonly opcional?: boolean;
  readonly ajuda?: string;
  /** Roda AO SAIR do campo. Devolve a mensagem, ou `null` quando esta bom. */
  readonly validar?: (valor: string) => string | null;
  /** `ctrl-k` faz Ctrl+K (ou Cmd+K) levar o foco para este campo. */
  readonly atalho?: 'ctrl-k';
  /** Filtro aplicado muda de aparencia (p.16). */
  readonly aplicado?: boolean;
  readonly tipo?: TipoDeCampo;
  /**
   * O que o gerenciador de senhas do navegador deve entender que este campo e
   * (`username`, `current-password`, …). Sem isto, o login nao e reconhecido e
   * todo mundo passa a escolher senha curta para conseguir digita-la.
   */
  readonly autoComplete?: string;
  /**
   * O `name` do campo. O gerenciador de senhas casa o campo guardado com o da
   * proxima visita pelo `name` e pelo `id`; sem os dois, o `id` gerado pelo
   * React (`«r5»`) muda com a arvore e o formulario de entrada nao e
   * reconhecido de uma visita para a outra (31/10/2026).
   */
  readonly nome?: string;
  /** Um `id` estavel no lugar do gerado, pelo mesmo motivo do `nome`. */
  readonly idFixo?: string;
  /**
   * O teto de caracteres do campo, quando o SERVIDOR tem um.
   *
   * Sem ele, colar um texto longo passa pela tela e volta como a recusa do
   * validador do servidor — que, quando alguem esquece a mensagem propria, e
   * uma frase em ingles de biblioteca. O `maxLength` nao substitui a validacao
   * do outro lado; ele evita a ida inutil.
   */
  readonly maxLength?: number;
}) {
  const idGerado = useId();
  const id = idFixo ?? idGerado;
  const campo = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState<string | null>(null);
  /* O OLHO DA SENHA (26/10/2026), e o campo o põe SOZINHO pelo tipo: campo de
     senha novo nasce com ele sem ninguém lembrar. Começa escondido — mostrar
     é o gesto deliberado de quem quer conferir, e não o padrão. */
  const [senhaVisivel, setSenhaVisivel] = useState(false);
  const ehSenha = tipo === 'senha';

  useEffect(() => {
    if (atalho !== 'ctrl-k') return;

    function aoTeclar(evento: KeyboardEvent) {
      /* `metaKey` entra junto porque o mesmo atalho precisa funcionar no Mac
         de quem abrir o sistema de fora da obra. */
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 'k') {
        evento.preventDefault();
        campo.current?.focus();
        campo.current?.select();
      }
    }

    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [atalho]);

  return (
    <Campo
      id={id}
      rotulo={rotulo}
      opcional={opcional}
      erro={erro}
      {...(ajuda ? { ajuda } : {})}
    >
      <div className="relative">
        <input
          id={id}
          ref={campo}
          type={ehSenha && senhaVisivel ? 'text' : TIPO_HTML[tipo]}
          {...(autoComplete ? { autoComplete } : {})}
          {...(nome ? { name: nome } : {})}
          {...(maxLength === undefined ? {} : { maxLength })}
          value={valor}
          placeholder={placeholder ?? ''}
          /* Abaixo de 768 px a dica do atalho sai (nao ha teclado de mesa, e
             ela cobria o texto do campo estreito), e o recuo dela tambem. */
          /* O recuo à direita abre espaço para o olho: sem ele o fim da senha
             passaria POR BAIXO do botão. Campo nunca é senha e atalho ao mesmo
             tempo, então os dois recuos não competem. */
          className={`controle w-full ${atalho ? 'pr-20 max-[768px]:pr-3' : ''} ${ehSenha ? 'pr-10' : ''} ${aplicado ? 'border-marca bg-marca-fundo' : ''}`}
          onChange={(evento) => {
            /* Limpar o erro enquanto se digita nao e validar: e parar de
               apontar um problema que a pessoa ja esta consertando. */
            setErro(null);
            aoMudar(evento.target.value);
          }}
          onBlur={() => {
            setErro(validar ? validar(valor) : null);
          }}
          {...(erro ? { 'aria-invalid': true, 'aria-describedby': `${id}-erro` } : {})}
        />
        {ehSenha ? (
          <OlhoDaSenha
            visivel={senhaVisivel}
            aoAlternar={() => {
              setSenhaVisivel((v) => !v);
            }}
          />
        ) : null}
        {atalho ? (
          <kbd className="pointer-events-none absolute inset-y-0 right-3 my-auto flex h-6 items-center rounded-marcador border border-borda bg-rebaixada px-2 txt-legenda mono text-tinta-secundaria max-[768px]:hidden">
            Ctrl K
          </kbd>
        ) : null}
      </div>
    </Campo>
  );
}

export interface OpcaoDeSelecao {
  readonly valor: string;
  readonly rotulo: string;
  /**
   * Segunda linha da opção, em 12 px (p.27): a descrição e a obra da máquina,
   * a função da pessoa. Entra também na busca.
   */
  readonly detalhe?: string;
  /**
   * A foto da opção, em miniatura de 32 px (a URL; a assinada da frota vence
   * em 5 minutos, e a que falha cai no `icone`). Só onde a coisa TEM foto: a
   * máquina.
   */
  readonly imagem?: string;
  /** O ícone do vocabulário (p.19) no bloco do tom neutro: o tipo da máquina no modelo e na configuração. */
  readonly icone?: LucideIcon;
  /** As iniciais no avatar redondo (p.24): a pessoa, que não tem foto no sistema. */
  readonly iniciais?: string;
  /**
   * As ETIQUETAS da opção (pedido do dono, 31/10/2026: "colocar como tags o
   * código interno e a placa, usar cores nessas tags. Esse tipo de cuidado
   * evita erros"): os identificadores secundários que a pessoa confere de
   * olho, ao lado do rótulo, cada um num tom fixo (`TomDaEtiqueta`). Entram
   * na busca e no `title` da opção.
   */
  readonly etiquetas?: readonly EtiquetaDaOpcao[];
  /**
   * O QUE O ADESIVO DA COISA TRAZ (decisão 64, 31/10/2026): o `id` e o código
   * impresso. Com ele, o campo com `adesivo` acha a opção pelo QR lido na
   * câmera, na foto ou no leitor que digita (`lib/leitura-do-adesivo`), mesmo
   * quando o `valor` da opção é outro (o lançamento usa o código). Quem monta
   * é `opcaoDaMaquina`.
   */
  readonly adesivo?: AlvoDoAdesivo;
}

/**
 * O tom de cada TIPO de etiqueta, e sempre o mesmo para o mesmo tipo: a cor
 * diz o que o texto é antes de a pessoa lê-lo (o "CB-07" é o código interno
 * porque é azul, o "ABC1D23" é a placa porque é escuro).
 *
 * - `informacao` — o código interno, na tinta do azul de informação. É
 *   etiqueta de raio 8 e sem ponto: não se confunde com o selo `Disponível`,
 *   que é pílula com ponto (p.25).
 * - `escuro` — a placa, no preto da tinta com o texto do card: lembra a
 *   placa do veículo e inverte sozinho no tema escuro.
 * - `neutro` — a etiqueta de atributo do manual (p.25: "neutra, raio 8"), no
 *   fundo rebaixado: o produto do tanque e o que mais for atributo.
 *
 * Laranja fica de fora (marca e ação), e os tons de alerta também (vermelho e
 * âmbar dizem que algo está errado, e uma placa não é alerta).
 */
export type TomDaEtiqueta = 'informacao' | 'escuro' | 'neutro';

export interface EtiquetaDaOpcao {
  readonly texto: string;
  readonly tom: TomDaEtiqueta;
  /** O que o texto é, por extenso: "Código interno", "Placa". Vai na dica e no leitor de tela. */
  readonly titulo?: string;
}

/**
 * O campo de escolha (p.23, p.31): o gatilho com a cara do campo e a lista
 * colada a ele, com foto, ícone ou avatar na opção que tem — a lista nativa
 * do `<select>` não mostra nenhum dos três (pedido do dono, 20/10/2026). O
 * desenho e o teclado estão em `ListaDeEscolha`.
 */
export function CampoSelecao({
  id: idExterno,
  rotulo,
  valor,
  aoMudar,
  opcoes,
  rotuloVazio,
  opcional = false,
  aplicado = false,
  ajuda,
  erro,
  desabilitado = false,
  opcaoUnica: regraDaUnica,
  porQueUnica,
  adesivo,
}: {
  /**
   * So quando outro elemento precisa APONTAR para este campo — um botao de
   * estado vazio que leva o foco ate ele, por exemplo. Sem isto, o `useId`
   * gera um id que ninguem de fora tem como escrever. Nos outros casos, deixe
   * em branco: id escrito a mao que se repete na pagina quebra o `label`.
   */
  readonly id?: string;
  readonly rotulo: string;
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly opcoes: readonly OpcaoDeSelecao[];
  /** Texto da opcao que representa "sem filtro". Ex.: "Todos". */
  readonly rotuloVazio?: string;
  readonly opcional?: boolean;
  readonly aplicado?: boolean;
  /** Frase completa, com ponto final, abaixo do campo. Some quando ha erro. */
  readonly ajuda?: string;
  /** Diz O QUE FAZER, nao so o que deu errado. */
  readonly erro?: string | null;
  readonly desabilitado?: boolean;
  /** O campo de opção única (`lib/escolha-unica.ts`): ver `ListaDeEscolha`. */
  readonly opcaoUnica?: boolean;
  /**
   * Por que sobrou uma opção só, na ajuda do campo travado: "Único tanque
   * liberado para o seu papel." Sem ela, "Única opção."
   */
  readonly porQueUnica?: string;
  /** O campo da máquina lê o adesivo: ver `ListaDeEscolha`. */
  readonly adesivo?: boolean | { readonly porQueFora?: string };
}) {
  const gerado = useId();
  const id = idExterno ?? gerado;
  /* A AJUDA DO CAMPO TRAVADO: a mesma conta que a `ListaDeEscolha` faz para
     travar, sem o preenchimento (quem preenche é ela, uma vez). */
  const travada =
    !desabilitado &&
    opcaoUnica({ opcoes: opcoes.map((o) => o.valor), obrigatoria: regraDaUnica ?? obrigatoriaNaMesa({ opcional, aplicado, rotuloVazio }) }) !== null;
  const ajudaFinal = travada ? [porQueUnica ?? AJUDA_DA_OPCAO_UNICA, ajuda].filter(Boolean).join(' ') : ajuda;

  return (
    <Campo id={id} rotulo={rotulo} opcional={opcional} erro={erro ?? null} {...(ajudaFinal ? { ajuda: ajudaFinal } : {})}>
      <ListaDeEscolha
        id={id}
        rotulo={rotulo}
        valor={valor}
        aoMudar={aoMudar}
        opcoes={opcoes}
        rotuloVazio={rotuloVazio}
        aplicado={aplicado}
        desabilitado={desabilitado}
        opcaoUnica={regraDaUnica ?? obrigatoriaNaMesa({ opcional, aplicado, rotuloVazio })}
        idDoErro={erro ? `${id}-erro` : undefined}
        {...(adesivo === undefined ? {} : { adesivo })}
      />
    </Campo>
  );
}
